import { GuestOccupancyF, ReservationF } from '../../../types/reservation-detail.types'
import { ReservationCheckInValidation, GuestOccupancyCheckInWarning, ReservedResourceCheckInValidation } from './check.in.types'
import { compareDates, getDayDiff, isDateBetween, isInAgeRange, isValidDate } from 'src/utils/date'
import { GuestAddressFragment, GuestType, ReservedResourcePhase } from 'src/api/api.generated'
import { isGuarantor } from '../../../utils/guest-occupancy'
import { TFunction } from 'i18next'
import { Context } from '../../../context/reservation-context.types'

export function validateCheckIn(reservation: ReservationF, hotelDate: Date, context?: Context, reservedResourceIds?: string[]): ReservationCheckInValidation {
  const isGroup = reservation.isGroup
  const reservedResources = (reservation.reservedResources ?? []) //
    .filter((rr) => {
      return reservedResourceIds === undefined || reservedResourceIds.includes(rr.id)
    })

  const noResources = reservedResources.length <= 0

  if (reservedResources.length <= 0) {
    return {
      isGroup,
      warning: {
        wrongGuestType: false,
        moreThanOneGuarantor: false,
        missingGuarantor: false,
        missingNotGuarantor: false,
      },
      error: {
        noResources: noResources,
      },
      reservedResources: [],
    }
  }

  const dayBeforeHotelDate = new Date(hotelDate.getFullYear(), hotelDate.getMonth(), hotelDate.getDate() - 1)

  const canDoCheckin = (start: Date, end: Date) => {
    return compareDates(dayBeforeHotelDate, start) <= 0 && compareDates(hotelDate, end) <= 0
  }

  const checkinableReservedResources = reservedResources.filter((rr) => canDoCheckin(rr.start, rr.end))

  const guestOccupancies = checkinableReservedResources.reduce((p: GuestOccupancyF[], v) => {
    return [...p, ...(v.guestOccupancies ?? [])]
  }, [])

  const wantToCheckingInGuests = guestOccupancies.filter(
    (go) =>
      go.phase === ReservedResourcePhase.RESERVATION && //
      canDoCheckin(go.start, go.end) &&
      !isMissingCheckInRequiredData(go),
  )

  const guarantorType = isGroup ? GuestType.GROUP_HEAD : GuestType.HEAD_OF_FAMILY
  const notGuarantorType = isGroup ? GuestType.GROUP_MEMBER : GuestType.FAMILY

  const wrongGuestType = isGroup
    ? wantToCheckingInGuests.some((go) => go.type === GuestType.HEAD_OF_FAMILY || go.type === GuestType.FAMILY)
    : wantToCheckingInGuests.some((go) => go.type === GuestType.GROUP_HEAD || go.type === GuestType.GROUP_MEMBER)

  const todayGuests = guestOccupancies.filter((go) => {
    return (
      (go.phase === ReservedResourcePhase.RESERVATION && compareDates(go.start, hotelDate) >= 0) || //
      (go.phase === ReservedResourcePhase.CHECKIN && compareDates(go.start, hotelDate) >= 0)
    )
  })

  const guarantors = todayGuests.filter((go) => go.type === guarantorType)
  const notGuarantors = todayGuests.filter((go) => go.type === notGuarantorType)

  // ---- new implementation ---
  // TODO: Ammette più di un garante solo se sono su reserved resource diverse.

  // const rrGuarantors = checkinableReservedResources.map((rr) => {
  //   const wantToCheckingInGuests = rr.guestOccupancies?.filter(
  //     (go) =>
  //       go.phase === ReservedResourcePhase.RESERVATION && //
  //       isDateBetween(hotelDate, go.start, go.end) &&
  //       !isMissingCheckInRequiredData(go),
  //   )

  //   const rrTodayGuests = wantToCheckingInGuests?.filter((go) => {
  //     return (
  //       (go.phase === ReservedResourcePhase.RESERVATION && compareDates(go.start, hotelDate) <= 0 && !isMissingCheckInRequiredData(go)) || //
  //       (go.phase === ReservedResourcePhase.CHECKIN && compareDates(go.start, hotelDate) >= 0)
  //     )
  //   })

  //   return {
  //     reservedResourceId: rr.id,
  //     guarantors: rrTodayGuests?.filter((go) => go.type === guarantorType).length ?? 0,
  //   }
  // })

  // const moreThanOneGuarantor = rrGuarantors.some((rr) => rr.guarantors >= 2)

  // ---- end new implementation ---

  let moreThanOneGuarantor = guarantors.length >= 2

  if (moreThanOneGuarantor && !isGroup) {
    moreThanOneGuarantor = checkinableReservedResources.some((reservedResource) => reservedResource?.guestOccupancies?.filter((guestOccupancy) => guestOccupancy.type === guarantorType).length !== 1)
  }
  const missingGuarantor = notGuarantors.length >= 1 && guarantors.length <= 0
  const missingNotGuarantor = guarantors.length >= 1 && notGuarantors.length <= 0

  const checkingInGuests = wantToCheckingInGuests.filter((go) => {
    if (go.type === GuestType.SINGLE_GUEST) return true

    if (moreThanOneGuarantor || missingGuarantor || missingNotGuarantor) return false

    return isGroup ? go.type === GuestType.GROUP_HEAD || go.type === GuestType.GROUP_MEMBER : go.type === GuestType.HEAD_OF_FAMILY || go.type === GuestType.FAMILY
  })

  const reservedResourceValidations: ReservedResourceCheckInValidation[] = reservedResources.map((rr) => {
    const missingResource = rr.currentResourceOccupancy.resource ? false : true
    const dataHasChanged = context?.reservedResources.get(rr.id)?.hasChanged ?? false

    const guestOccupancyWarnings = (rr.guestOccupancies ?? []).map((go) => getGuestOccupancyWarning(go, hotelDate, checkingInGuests))
    const checkingInGuestIds = (rr.guestOccupancies ?? [])
      .filter((go) => {
        return checkingInGuests.some((cgo) => cgo.guest.id === go.guest.id)
      })
      .map((go) => go.guest.id)
    const alreadyCheckedInGuests = rr.guestOccupancies?.filter((go) => go.phase !== ReservedResourcePhase.RESERVATION)?.length ?? 0
    const emptyCheckIn = checkingInGuestIds.length <= 0 && alreadyCheckedInGuests < rr.adults + rr.children
    const partialCheckIn = !emptyCheckIn && checkingInGuestIds.length + alreadyCheckedInGuests < rr.adults + rr.children

    const pastStartDate = compareDates(rr.start, hotelDate) < 0
    const checkingInAlreadyCheckedInResourceWithNoGuests = rr.phase !== ReservedResourcePhase.RESERVATION && checkingInGuestIds.length <= 0

    const checkoutDateExpired = compareDates(rr.end, hotelDate) < 0
    const checkinDateExpired = compareDates(hotelDate, rr.start) > 0 && getDayDiff(rr.start, hotelDate) > 1

    const hasError = missingResource || checkoutDateExpired || dataHasChanged || checkingInAlreadyCheckedInResourceWithNoGuests || checkinDateExpired

    const thereAreCheckinableRR = checkinableReservedResources.length > 0

    let wrongBirthDates = false
    const rrGuestsBirthDates: Date[] = []
    rr.guestOccupancies?.forEach((go) => {
      const birthDate = go.guest.person?.birthDate
      if (birthDate) rrGuestsBirthDates.push(birthDate)
    })
    if (rr.adults + rr.children === rrGuestsBirthDates.length) {
      wrongBirthDates = !checkAgeRanges(
        rrGuestsBirthDates,
        rr.childOccupancies?.map((co) => ({ id: co.child.id, minAge: co.child.minAge, maxAge: co.child.maxAge, quantity: co.quantity, noOccupancy: co.child.noOccupancy })) ?? [],
        rr.adults,
        hotelDate,
      )
    }

    return {
      reservedResourceId: rr.id,
      error: {
        missingResource,
        checkoutDateExpired,
        dataHasChanged,
        checkingInAlreadyCheckedInResourceWithNoGuests,
        checkinDateExpired,
      },
      warning: {
        pastStartDate: !hasError && pastStartDate,
        partialCheckIn: !hasError && partialCheckIn,
        emptyCheckIn: !hasError && emptyCheckIn,
        wrongBirthDates,
      },
      guestOccupancyWarnings: thereAreCheckinableRR ? guestOccupancyWarnings : [],
      checkingInGuestIds: hasError ? [] : checkingInGuestIds,
    }
  })

  return {
    isGroup,
    warning: {
      wrongGuestType,
      moreThanOneGuarantor,
      missingGuarantor,
      missingNotGuarantor,
    },
    error: {
      noResources,
    },
    reservedResources: reservedResourceValidations,
  }
}

export function isMissingCheckInRequiredData(guestOccupancy: GuestOccupancyF): boolean {
  const guest = guestOccupancy.guest
  if (!guest.person?.gender) return true
  if (!isValidDate(guest.person?.birthDate)) return true
  if (!isAddressValidForCheckIn(guest.person?.birthAddress)) return true
  if (!isAddressValidForCheckIn(guest.address)) return true
  if (!guest.person?.nationalityId) return true
  if (isGuarantor(guestOccupancy.type) && !guest.person?.identificationDocument) return true
  return false
}

function isAddressValidForCheckIn(address: GuestAddressFragment | undefined | null): boolean {
  if (!address) return false
  const country = address.country
  if (!country) return false
  if (country.isoCode === 'IT' && (!address.municipalityId || !address.province)) return false
  return true
}

function getGuestOccupancyWarning(go: GuestOccupancyF, hotelDate: Date, checkingInGuests: GuestOccupancyF[]): GuestOccupancyCheckInWarning {
  if (go.phase !== ReservedResourcePhase.RESERVATION || !isDateBetween(hotelDate, go.start, go.end)) {
    return {
      missingRequiredData: false,
      wrongGuestType: false,
    }
  }

  const missingRequiredData = isMissingCheckInRequiredData(go)
  const wrongGuestType = !missingRequiredData && !checkingInGuests.some((cgo) => cgo.guest.id === go.guest.id)
  return {
    missingRequiredData,
    wrongGuestType,
  }
}

export function getResevationWarningMessage(t: TFunction, validation: ReservationCheckInValidation): string | null {
  if (validation.warning.wrongGuestType || validation.warning.moreThanOneGuarantor || validation.warning.missingGuarantor || validation.warning.missingNotGuarantor) {
    const messages: string[] = []
    if (validation.warning.wrongGuestType && validation.isGroup) {
      messages.push(t('pms.booking.reservation.check_in.warning.wrong_group_guest_type'))
    }
    if (validation.warning.wrongGuestType && !validation.isGroup) {
      messages.push(t('pms.booking.reservation.check_in.warning.wrong_family_guest_type'))
    }
    if (validation.warning.moreThanOneGuarantor && validation.isGroup) {
      messages.push(t('pms.booking.reservation.check_in.warning.more_than_one_group_guarantor'))
    }
    if (validation.warning.moreThanOneGuarantor && !validation.isGroup) {
      messages.push(t('pms.booking.reservation.check_in.warning.more_than_one_family_guarantor'))
    }
    if (validation.warning.missingGuarantor && validation.isGroup) {
      messages.push(t('pms.booking.reservation.check_in.warning.missing_group_guarantor'))
    }
    if (validation.warning.missingGuarantor && !validation.isGroup) {
      messages.push(t('pms.booking.reservation.check_in.warning.missing_family_guarantor'))
    }
    if (validation.warning.missingNotGuarantor && validation.isGroup) {
      messages.push(t('pms.booking.reservation.check_in.warning.missing_group_not_guarantor'))
    }
    if (validation.warning.missingNotGuarantor && !validation.isGroup) {
      messages.push(t('pms.booking.reservation.check_in.warning.missing_family_not_guarantor'))
    }
    if (validation.warning.moreThanOneGuarantor || validation.warning.missingGuarantor || validation.warning.missingNotGuarantor) {
      messages.push(t('pms.booking.reservation.check_in.warning.single_guest'))
    }
    return messages.join(' ')
  } else {
    return null
  }
}

export function getGuestCheckInWarningMessage(t: TFunction, warning: GuestOccupancyCheckInWarning | undefined, hasChanged: boolean): string | null {
  if (hasChanged) return null
  if (!warning) return null
  if (warning.wrongGuestType || warning.missingRequiredData) {
    const messages: string[] = []
    if (warning.wrongGuestType) {
      messages.push(t('pms.booking.reservation.check_in.guest_warning.wrong_type'))
    }
    if (warning.missingRequiredData) {
      messages.push(t('pms.booking.reservation.check_in.guest_warning.missing_required_data'))
    }
    messages.push(t('pms.booking.reservation.check_in.guest_warning.not_checking_in_guest'))
    return messages.join(' ')
  } else {
    return null
  }
}

type RequiredChildOccupancy = { id: string; minAge: number; maxAge: number; quantity: number; noOccupancy: boolean }
type AgeRange = { requiredQuantity: number; effectiveQuantity: number; noOccupancy: boolean }

/**
 * Group the birth dates that correspond to the age groups. Dates that do not match any range will be counted as an additional group with NULL ID
 * @param birthDates
 * @param requiredChildOccupancy
 * @param adults
 * @param hotelDate
 */
function groupByAgeRange(birthDates: Date[], requiredChildOccupancy: RequiredChildOccupancy[], adults: number, hotelDate: Date) {
  const ageRange = new Map<string | null, AgeRange>()
  requiredChildOccupancy.forEach((item) => {
    ageRange.set(item.id, { requiredQuantity: item.quantity, effectiveQuantity: 0, noOccupancy: item.noOccupancy })
  })
  ageRange.set(null, { requiredQuantity: adults, effectiveQuantity: 0, noOccupancy: false })

  birthDates.reduce((previousValue, currentValue) => {
    const ageRangeId = requiredChildOccupancy.find((i) => isInAgeRange(currentValue, i.minAge, i.maxAge))?.id || null
    return incrementEffectiveQuantity(previousValue, ageRangeId)
  }, ageRange)

  return ageRange
}

/**
 * Increment the age range by id
 * @param ageRange
 * @param id
 * @returns
 */
function incrementEffectiveQuantity(ageRange: Map<string | null, AgeRange>, id: string | null) {
  const oldItem = ageRange.get(id)
  if (oldItem) {
    ageRange.set(id, { ...oldItem, effectiveQuantity: oldItem.effectiveQuantity + 1 })
  }
  return ageRange
}

/**
 * Check if all guests have birth dates that match the expected age ranges
 * @param birthDates
 * @param requiredChildOccupancy
 * @param adults
 * @param hotelDate
 * @returns
 */
function checkAgeRanges(birthDates: Date[], requiredChildOccupancy: RequiredChildOccupancy[], adults: number, hotelDate: Date) {
  const ageRangeGroups = groupByAgeRange(birthDates, requiredChildOccupancy, adults, hotelDate)

  let check = true

  for (const [key, value] of ageRangeGroups) {
    if (check && value.effectiveQuantity !== value.requiredQuantity && !value.noOccupancy) {
      check = false
      break
    }
  }

  return check
}
